'use client';

import { CrmPointRule, User } from '@/api/types/public';
import { useJump } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import { useAuth } from '@/providers/auth-provider';
import { ProviderType } from '@/reporting';
import {
  ACCOUNT_WHITE_LIST_NEW,
  LocaleType,
  NAVIGATION_KEYS,
  NavigationKey,
} from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { Box, Collapse, List, Tooltip } from '@mui/material';
import {
  CollapseArrowUpOutlined,
  DashboardOutlined,
  MembershipOutlined,
  MyAccountOutlined,
  MyAssetsOutlined,
  OrderOutlined,
  PersonalizationOutlined,
} from '@zeelool/icon';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';

interface NavItem {
  key: NavigationKey;
  title: string;
  title_en?: string;
  href?: string;
  icon?: React.ReactNode;
  highlight?: boolean;
  gaEventName?: string;
  children?: Array<Omit<NavItem, 'icon'>>;
}

interface TFunction {
  (key: string, options?: any): string;
}

// TODO: 这里的语言包后续补上
// 初始导航项定义
// 这里的页面顺序别随便调整哈，后面的逻辑依赖于这个顺序
const getInitialNavItems = (t: TFunction): NavItem[] => [
  {
    key: NAVIGATION_KEYS.DASHBOARD,
    title: t('navigation.dashboard'),
    title_en: 'Info Dashboard',
    href: '/account/my-account',
    icon: <DashboardOutlined className="text-primary-text text-[18px]" />,
  },
  {
    key: NAVIGATION_KEYS.MY_ACCOUNT,
    title: t('navigation.myAccount.title'),
    title_en: 'My Account',
    icon: <MyAccountOutlined className="text-primary-text text-[18px]" />,
    children: [
      {
        key: NAVIGATION_KEYS.ACCOUNT_SETTINGS,
        title: t('navigation.myAccount.accountSetting'),
        title_en: 'Account Setting',
        href: '/account/account-settings',
      },
      {
        key: NAVIGATION_KEYS.PRESCRIPTIONS,
        title: t('navigation.myAccount.prescription'),
        title_en: 'Prescription',
        href: '/account/my-prescriptions',
      },
      {
        key: NAVIGATION_KEYS.ADDRESS_BOOK,
        title: t('navigation.myAccount.address'),
        title_en: 'Address',
        href: '/account/my-address-book',
      },
      {
        key: NAVIGATION_KEYS.REVIEWS,
        title: t('navigation.myAccount.reviews'),
        title_en: 'Reviews',
        href: '/account/my-reviews',
      },
      {
        key: NAVIGATION_KEYS.ACCOUNT_SECURITY,
        title: t('navigation.myAccount.accountSecurity'),
        title_en: 'Account Security',
        href: '/account/account-security',
      },
      {
        key: NAVIGATION_KEYS.QUESTIONNAIRES,
        title: t('navigation.myAccount.questionnaire'),
        title_en: 'Questionnaire',
        href: '/account/questionnaires-list',
      },
    ],
  },
  {
    key: NAVIGATION_KEYS.MEMBERSHIP_HUB,
    title: t('navigation.membershipHub.title'),
    title_en: 'Membership Hub',
    icon: <MembershipOutlined className="text-primary-text text-[18px]" />,
    children: [
      {
        key: NAVIGATION_KEYS.ZEELOOL_REWARDS,
        title: t('navigation.membershipHub.zeeloolRewards'),
        title_en: 'ZEELOOL Rewards',
        href: '/account/zeelool-rewards',
      },
      {
        key: NAVIGATION_KEYS.REWARDS_STORE,
        title: t('navigation.membershipHub.rewardsStore'),
        title_en: 'Rewards Store',
        href: '/account/points-mall',
      },
    ],
  },
  {
    key: NAVIGATION_KEYS.ORDER_MANAGEMENT,
    title: t('navigation.orderManagement.title'),
    title_en: 'Order Management',
    icon: <OrderOutlined className="text-primary-text text-[18px]" />,
    children: [
      {
        key: NAVIGATION_KEYS.MY_ORDERS,
        title: t('navigation.orderManagement.myOrders'),
        title_en: 'My Orders',
        href: '/account/my-orders',
      },
    ],
  },
  {
    key: NAVIGATION_KEYS.MY_ASSETS,
    title: t('navigation.myAssets.title'),
    title_en: 'My Assets',
    icon: <MyAssetsOutlined className="text-primary-text text-[18px]" />,
    children: [
      {
        key: NAVIGATION_KEYS.TICKETS,
        title: t('navigation.myAssets.myTickets'),
        title_en: 'My Tickets',
        href: '/account/tickets-history',
      },
      {
        key: NAVIGATION_KEYS.COUPONS,
        title: t('navigation.myAssets.myCoupons'),
        title_en: 'My Coupons',
        href: '/account/my-coupons',
      },
      {
        key: NAVIGATION_KEYS.STORE_CREDIT,
        title: t('navigation.myAssets.myStoreCredit'),
        title_en: 'My Store Credit',
        href: '/account/my-wallet',
      },
      {
        key: NAVIGATION_KEYS.MY_CARD,
        title: t('navigation.myAssets.myCard'),
        title_en: 'My Card',
        href: '/account/my-card',
      },
    ],
  },
  {
    key: NAVIGATION_KEYS.PERSONALIZATION,
    title: t('navigation.personalization.title'),
    title_en: 'Personalization',
    icon: <PersonalizationOutlined className="text-primary-text text-[18px]" />,
    children: [
      {
        key: NAVIGATION_KEYS.WISHLIST,
        title: t('navigation.personalization.myWishlist'),
        title_en: 'My Wishlist',
        href: '/account/my-wishlist',
      },
      {
        key: NAVIGATION_KEYS.REFER_FRIEND,
        title: t('navigation.personalization.referAFriend'),
        title_en: 'Refer a Friend',
        href: '/account/refer-a-friend',
      },
      {
        key: NAVIGATION_KEYS.RECENTLY_VIEWED,
        title: t('navigation.personalization.recentlyViewed'),
        title_en: 'Recently Viewed',
        href: '/account/recently-viewed',
      },
      {
        key: NAVIGATION_KEYS.COMMUNICATION_PREFERENCES,
        title: t('navigation.personalization.communicationPreferences'),
        title_en: 'Communication Preferences',
        href: '/account/communication-preferences',
      },
    ],
  },
];

interface AccountLayoutClientProps {
  children: React.ReactNode;
  pointRules: CrmPointRule;
  userInfo: User;
}
// Define the variants for the nav item container
const navItemContainerStyles = cva(
  'relative leading-[36px] flex items-center cursor-pointer text-primary-text',
  {
    variants: {
      isMyAccount: {
        true: 'pl-[16px]',
        false: 'pl-[40px]',
      },
      isActive: {
        true: '',
        false: '',
      },
      isActiveMyAccount: {
        true: 'bg-[rgba(186,172,255,0.23)] rounded-[8px]',
        false: '',
      },
    },
  },
);

// Define the variants for the nav item text
const navItemTextStyles = cva(
  'hover:text-primary-purple hover:font-bs whitespace-nowrap overflow-hidden text-ellipsis',
  {
    variants: {
      active: {
        true: 'font-bs text-primary-purple',
        false: 'font-br',
      },
      isMyAccount: {
        true: '!font-bb',
        false: '',
      },
    },
    compoundVariants: [
      {
        active: true,
        isMyAccount: true,
        className: 'font-bs !text-primary-text', // Override the blue color for My Account when active
      },
    ],
  },
);

const navItemLocaleStyles = cva('font-bb overflow-hidden whitespace-nowrap text-ellipsis', {
  variants: {
    locale: {
      en: '',
      de: 'w-[164px]',
      es: 'w-[164px]',
      fr: 'w-[184px]',
      it: 'w-[164px]',
      pt: 'w-[164px]',
      fi: 'w-[164px]',
      nl: 'w-[164px]',
    },
  },
  defaultVariants: {
    locale: 'en',
  },
});

const pointTagStyles = cva('absolute top-[0px] w-[100px]', {
  variants: {
    locale: {
      en: 'left-[170px]',
      de: 'left-[180px]',
      es: 'left-[170px]',
      fr: 'left-[230px]',
      it: 'left-[170px]',
      pt: 'left-[170px]',
      fi: 'left-[170px]',
      nl: 'left-[170px]',
    },
  },
  defaultVariants: {
    locale: 'en',
  },
});

const navStyles = cva(
  'overflow-hidden w-[265px] p-[16px_32px_24px_0] flex-shrink-0 border-r border-[#d4d4d4] overflow-y-auto',
  {
    variants: {
      locale: {
        en: 'w-[265px]',
        de: 'w-[265px]',
        es: 'w-[265px]',
        fr: 'w-[300px]',
        it: 'w-[265px]',
        pt: 'w-[265px]',
        fi: 'w-[265px]',
        nl: 'w-[265px]',
      },
    },
    defaultVariants: {
      locale: 'en',
    },
  },
);

export default function AccountLayoutClient({
  children,
  pointRules,
  userInfo,
}: AccountLayoutClientProps) {
  const pathname = usePathname();
  const { trackEvent } = useReporting();

  const toJumpTarget = useJump();

  const { isAuthenticated } = useAuth();
  const clientUserInfo = useStore((state) => state.userInfo);
  const t = useTranslations('account.myAccount');
  const locale = useLocale() as LocaleType;
  const token = useStore((state) => state.token);
  const [isDisplayNav, setIsDisplayNav] = useState(!!userInfo?.id);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({
    [NAVIGATION_KEYS.MY_ACCOUNT]: true,
    [NAVIGATION_KEYS.ORDER_MANAGEMENT]: true,
    [NAVIGATION_KEYS.MY_ASSETS]: true,
    [NAVIGATION_KEYS.PERSONALIZATION]: true,
    [NAVIGATION_KEYS.MEMBERSHIP_HUB]: true,
  });

  const [subscriptionId, setSubscriptionId] = useState(userInfo?.subscription_id);
  // 这个是服务端初始化的值
  const [currentPerfectPoint, setCurrentPerfectPoint] = useState(userInfo?.perfect_point || 0);

  useEffect(() => {
    setCurrentPerfectPoint(clientUserInfo.perfect_point);
  }, [clientUserInfo.perfect_point]);

  useEffect(() => {
    setSubscriptionId(clientUserInfo?.subscription_id);
  }, [clientUserInfo?.subscription_id]);

  // 添加自动续订管理菜单项
  const modifiedNavItems = useMemo(() => {
    const initialItems = getInitialNavItems(t);
    if (initialItems.length === 0) return initialItems;

    // TODO:神奇了，这里用cloneDeep会报死循环
    const updatedNavItems = [...initialItems];

    if (env.NEXT_PUBLIC_KOC_MODULE_ENABLE === 'true') {
      // 追加到最后一个子菜单项
      updatedNavItems[5].children.splice(3, 0, {
        key: NAVIGATION_KEYS.CONSUMER_AFFILIATE,
        title: t('navigation.personalization.consumerAffiliate'),
        href: '/account/koc-program',
      });
    }

    // 非VIP用户，个人中心的入口直接不展示, 已经是VIP用户，个人中心的入口保留
    if (env.NEXT_PUBLIC_ZING_MODULE_ENABLE === 'true' && userInfo?.type === 2) {
      updatedNavItems[2].children.splice(1, 0, {
        key: NAVIGATION_KEYS.ZING,
        title: t('navigation.membershipHub.zing'),
        href: '/account/zing',
      });
    }
    // 检查是否已经存在该菜单项
    const alreadyExists = updatedNavItems[2]?.children?.some?.(
      (item: NavItem) => item.key === NAVIGATION_KEYS.MANAGE_AUTO_RENEWAL,
    );

    if (subscriptionId) {
      if (!alreadyExists) {
        // 将菜单项添加到第三个位置
        updatedNavItems[2].children.splice(2, 0, {
          key: NAVIGATION_KEYS.MANAGE_AUTO_RENEWAL,
          title: t('navigation.membershipHub.manageAutoRenewal'),
          href: '/account/manage-subscription',
        });
      }
    } else if (alreadyExists) {
      // 将菜单项从第三个位置移除
      updatedNavItems[2].children = updatedNavItems[2].children.filter(
        (item: NavItem) => item.key !== NAVIGATION_KEYS.MANAGE_AUTO_RENEWAL,
      );
    }

    // 再判断推荐注册和推荐购买的页面
    const { reward } = pointRules;
    const hideReferAFriend =
      reward && reward.recommend_buy === 2 && reward && reward.recommend_reg === 2; // 推荐注册，推荐购买同时关闭，用户侧不展示 邀请好友入口

    if (hideReferAFriend && updatedNavItems[5]?.children) {
      updatedNavItems[5].children = updatedNavItems[5].children?.filter(
        (item) => item.key !== NAVIGATION_KEYS.REFER_FRIEND,
      );
    }

    // 只有 US 站点展示 Communication Preferences 入口
    if (env.NEXT_PUBLIC_SITE_NAME !== 'us' && updatedNavItems[5]?.children) {
      updatedNavItems[5].children = updatedNavItems[5].children?.filter(
        (item) => item.key !== NAVIGATION_KEYS.COMMUNICATION_PREFERENCES,
      );
    }

    return updatedNavItems;
  }, [subscriptionId, pointRules?.reward?.recommend_buy, pointRules?.reward?.recommend_reg, t]);

  useEffect(() => {
    if (token) {
      // 如果已经登录，则打开所有菜单
      setIsDisplayNav(true);
    }
  }, [token]);

  const handleToggle = (key: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isActive = (href?: string) => {
    if (!href) return false;
    // 兼容旧的链接
    if (pathname === '/account/order-detail' && href.startsWith('/account/my-orders')) {
      return true;
    } else if (pathname === '/account/write-review' && href.startsWith('/account/my-reviews')) {
      return true;
    } else if (
      pathname === '/account/add-prescription' &&
      href.startsWith('/account/my-prescriptions')
    ) {
      return true;
    }
    // 正常的新的链接
    return new RegExp(`^${href}`).test(pathname);
  };

  // 添加函数检查是否有选中的子项
  const hasActiveChild = (child?: NavItem[]) => {
    if (!child) return false;
    return child.some((ch) => isActive(ch.href) || hasActiveChild(ch.children));
  };

  const { deviceType } = useContext(ResponsiveContext);
  // 左侧导航点击
  const handleLinkClick = (firstTabName: string, secondTabName: string, btnName: string): void => {
    // 点击时置顶
    if (deviceType === EPlatform.WEB) {
      window.scrollTo(0, 0);
    }
    // 积分商城特殊处理 积分商城的secondTabName和btnName都显示为Points Mall
    let _secondTabName = secondTabName;
    let _btnName = btnName;

    if (secondTabName === t('navigation.membershipHub.rewardsStore')) {
      _secondTabName = 'Points Mall';
    }
    if (btnName === t('navigation.membershipHub.rewardsStore')) {
      _btnName = 'Points Mall';
    }

    if (btnName === 'Communication Preferences') {
      trackEvent(
        {
          eventName: 'AccountSetPageBtn_Click',
          properties: {
            btn_name: 'Communication Preferences',
          },
        },
        [ProviderType.SENSORS],
      );
    }

    trackEvent(
      {
        eventName: 'MinePageNavBtn_Click',
        properties: {
          first_tab_name: firstTabName,
          second_tab_name: _secondTabName,
          btn_name: _btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const renderNavItems = (items: NavItem[], level = 0, parentTitle = '') => {
    if (items.length === 0) return null;

    return (
      <List component="div" className="p-0">
        {items.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isOpen = openItems[item.key] || false;
          const hasActive = hasActiveChild(item.children);
          const isItemActive = isActive(item.href);
          const isMyAccount = item.href === '/account/my-account';

          return (
            <div key={item.key}>
              {item.href && !hasChildren ? (
                <Link
                  prefetch={false}
                  href={item.href}
                  onClick={() =>
                    handleLinkClick(
                      level === 0 ? item.title_en : parentTitle,
                      item.title_en,
                      item.title_en,
                    )
                  }
                  className="no-underline text-inherit"
                >
                  <div
                    className={navItemContainerStyles({
                      isMyAccount,
                      isActive: isItemActive,
                      isActiveMyAccount: isItemActive && isMyAccount,
                    })}
                  >
                    {item.icon && <div className="mr-[6px]">{item.icon}</div>}
                    <div
                      className={navItemTextStyles({
                        active: isItemActive,
                        isMyAccount,
                      })}
                    >
                      {userInfo?.customer_mark === 1 &&
                      item.title === t('navigation.myAccount.accountSetting') ? (
                        <Box className="absolute top-[15px] left-[28px] w-[6px] h-[6px] bg-primary-purple rounded-full" />
                      ) : null}
                      {item.title.length > 20 ? (
                        <Tooltip title={item.title} placement="top">
                          <span>{item.title}</span>
                        </Tooltip>
                      ) : (
                        item.title
                      )}
                      {userInfo?.is_show_point_tag &&
                      item.title === t('navigation.myAccount.accountSetting') &&
                      currentPerfectPoint > 0 ? (
                        <div
                          className={pointTagStyles({
                            locale,
                          })}
                        >
                          <div className="relative rounded-[2px] bg-primary-green py-[2px] px-[3px] text-[11px] text-primary-text inline-block leading-[1] before:content-[' '] before:absolute before:left-[-5px] before:top-1/2 before:h-0 before:w-0 before:border-t-[5px] before:border-t-transparent before:border-b-[5px] before:border-b-transparent before:border-r-[8.6px] before:border-r-[#ccff67] before:-translate-y-1/2">
                            + {currentPerfectPoint}{' '}
                            {currentPerfectPoint > 1
                              ? t('navigation.pointsTag.points')
                              : t('navigation.pointsTag.point')}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Link>
              ) : (
                <>
                  <div
                    onClick={() => handleToggle(item.key)}
                    className={`pl-[16px] h-[40px] rounded-[8px] flex items-center justify-between cursor-pointer ${
                      hasActive ? 'bg-[rgba(186,172,255,0.23)]' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      {item.icon && <div className="mr-6">{item.icon}</div>}
                      <div
                        className={navItemLocaleStyles({
                          locale,
                        })}
                      >
                        {item.title.length > 20 ? (
                          <Tooltip title={item.title} placement="top">
                            <span>{item.title}</span>
                          </Tooltip>
                        ) : (
                          item.title
                        )}
                      </div>
                    </div>
                    {hasChildren && (
                      <CollapseArrowUpOutlined
                        className={`text-secondary-text text-[14px] transition-transform duration-200 mr-[8px] ${
                          isOpen ? '' : 'rotate-180'
                        }`}
                      />
                    )}
                  </div>
                  {hasChildren && (
                    <Collapse
                      in={isOpen}
                      timeout="auto"
                      unmountOnExit
                      sx={{
                        '& .MuiList-root': {
                          paddingTop: '0px',
                          paddingBottom: '0px',
                        },
                      }}
                    >
                      {renderNavItems(item.children, level + 1, item.title_en)}
                    </Collapse>
                  )}
                </>
              )}
            </div>
          );
        })}
      </List>
    );
  };

  const [minWidth, setMinWidth] = useState('min-w-[640px]');
  // 注意：最小宽度不能大于 1000px。右侧1000px时整体宽度为 1347px，保证 1366px 时右侧没有滚动条
  useEffect(() => {
    switch (pathname) {
      case '/account/my-account':
        setMinWidth('min-w-[925px]');
        break;
      default:
        setMinWidth('min-w-[640px]');
        break;
    }
  }, [pathname]);

  useWhenTokenReady(() => {
    // 如果用户信息获取不到，并且当前路径不是白名单中的路径，则跳转到首页
    if (
      !token &&
      !isAuthenticated &&
      !ACCOUNT_WHITE_LIST_NEW.some((whitePath) => pathname?.startsWith(whitePath))
    ) {
      toJumpTarget({
        jump_type: 4,
        Jump_target: '/',
      });
    }
  }, [pathname, isAuthenticated, token, toJumpTarget]);

  return (
    <>
      <WebOnly>
        <div className="flex gap-[40px] min-h-[calc(100vh-60px)] w-[1520px] mx-auto max-[1600px]:w-full max-[1600px]:px-[24px]">
          <nav
            style={isDisplayNav ? { display: 'block' } : { display: 'none' }}
            className={navStyles({
              locale,
            })}
          >
            {renderNavItems(modifiedNavItems)}
          </nav>
          {!!userInfo?.id ||
          isAuthenticated ||
          ACCOUNT_WHITE_LIST_NEW.some((whitePath) => pathname?.startsWith(whitePath)) ? (
            <main
              className={`w-full ${isDisplayNav ? 'max-w-[1215px] min-w-[927px]' : 'max-w-[1600px]'} pt-[16px] pb-[24px] overflow-x-auto`}
            >
              <section id="account-layout-client" className={`${minWidth} relative`}>
                {children}
              </section>
            </main>
          ) : null}
        </div>
      </WebOnly>
      <WapOnly>
        {!!userInfo?.id ||
        isAuthenticated ||
        ACCOUNT_WHITE_LIST_NEW.some((whitePath) => pathname?.startsWith(whitePath)) ? (
          <div className="min-h-[100vh] relative flex flex-col">{children}</div>
        ) : null}
      </WapOnly>
    </>
  );
}
